import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { AtlasSite, DiveSite, ReverseGeocodeResponse } from '@/types/api';

export function useDiveSiteSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: ['dive-sites', 'search', query],
    queryFn: () =>
      api<{ items: DiveSite[] }>(`/api/v1/dive-sites/search?q=${encodeURIComponent(query)}&limit=20`),
    enabled: enabled && query.trim().length >= 2,
  });
}

export function useDiveSitesNearby(
  coords: { lat: number; lon: number } | null,
  radiusKm = 10,
  limit = 50,
) {
  return useQuery({
    queryKey: ['dive-sites', 'nearby', coords, radiusKm, limit],
    queryFn: () =>
      api<{ items: DiveSite[] }>(
        `/api/v1/dive-sites/nearby?lat=${coords!.lat}&lon=${coords!.lon}&radius_km=${radiusKm}&limit=${limit}`,
      ),
    enabled: coords !== null,
    staleTime: 60_000,
  });
}

/** Public endpoint – usable without auth on the marketing landing page. */
export function useShowcaseSites(
  coords: { lat: number; lon: number },
  radiusKm = 80,
  limit = 60,
) {
  return useQuery({
    queryKey: ['dive-sites', 'showcase', coords, radiusKm, limit],
    queryFn: () =>
      fetch(
        `/api/v1/dive-sites/showcase?lat=${coords.lat}&lon=${coords.lon}&radius_km=${radiusKm}&limit=${limit}`,
      ).then((r) => r.json() as Promise<{ items: DiveSite[] }>),
    staleTime: 5 * 60_000,
  });
}

/** Slim list of every site with coords – ~25 k items, used for the /sites atlas map. */
export function useAtlasSites() {
  return useQuery({
    queryKey: ['dive-sites', 'atlas'],
    queryFn: () => api<{ items: AtlasSite[] }>('/api/v1/dive-sites/atlas'),
    staleTime: 5 * 60_000,
    gcTime:   30 * 60_000,
  });
}

export function useMyDiveSites() {
  return useQuery({
    queryKey: ['dive-sites', 'mine'],
    queryFn: () => api<{ items: DiveSite[] }>('/api/v1/dive-sites/mine'),
    staleTime: 60_000,
  });
}

export function useDiveSite(id: number | undefined) {
  return useQuery({
    queryKey: ['dive-site', id],
    queryFn: () => api<DiveSite>(`/api/v1/dive-sites/${id}`),
    enabled: id !== undefined,
  });
}

export interface DiveSiteInput {
  title: string;
  description?: string | null;
  geoLatitude?: number | null;
  geoLongitude?: number | null;
  regionPath?: string[] | null;
  countryCode?: string | null;
  siteType?: string | null;
  difficulty?: string | null;
  entryType?: string | null;
  maxDepthMeters?: number | null;
  animals?: string[] | null;
}

export function useCreateDiveSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DiveSiteInput) =>
      api<DiveSite>('/api/v1/dive-sites', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dive-sites'] }),
  });
}

export function useReverseGeocode() {
  return useMutation({
    mutationFn: (coords: { lat: number; lon: number }) =>
      api<ReverseGeocodeResponse>(
        `/api/v1/dive-sites/reverse-geocode?lat=${coords.lat}&lon=${coords.lon}`,
      ),
  });
}
